import { Empty, Spin } from "antd";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useProject } from "../../App/state";
import { DisplayTreeNode } from "../../components/DisplayTree/types";
import { DualViewer } from "../../components/DualView";
import { LayoutWithSideDrawer } from "../../components/DualViewSideDrawer";
import { FloorplanViewer } from "../../components/DualViewSideDrawer/FloorplanViewer";
import SceneSelectionDropdowns from "../../components/SceneSelectionDropdowns";
import { BatchSelector } from "../../components/Selectors/BatchSelector";
import { SpaceSelector } from "../../components/Selectors/SpaceSelector";
import { SpaceSelectionModal } from "../../components/SpaceSelectionModal";
import { SpacesTree } from "../../components/SpacesTree/SpacesTree";
import { Shot } from "../../components/SpacesTree/tree-nodes";
import { SpotlightQA } from "../../components/SpotlightQA";
import { useGetAncestorsForSceneQuery } from "../../generated/types";
import styles from "./DualModePage.module.css";
import { Provider, useDualModeState } from "./state";
import { useFloorplanData } from "./useFloorplanData";

const DualModeContent = () => {
  const project = useProject();
  const {
    rootSpaceId,
    referenceBatch,
    loadDefaultScene,
    setNextScene,
    setPreviousScene,
    moveSceneUp,
    moveSceneDown,
    currentSceneId,
    setCurrentScene,
    focusOnSpotlight,
    setReferenceBatch,
    setComparisonBatch,
    setRootSpace,
    view,
    setView,
    comparisonBatch,
    focusedSpotlightId,
    // currentSceneFloorplanData,
    floorplans,
  } = useDualModeState();

  // Get the first available floorplan for the current root space
  const {
    data: newData,
    previousData,
    // loading,
    // error,
  } = useGetAncestorsForSceneQuery({
    variables: {
      ...project,
      sceneId: currentSceneId?.value,
    },
  });
  // Use old results until new ones have loaded.
  const sceneAncestors = newData ?? previousData;

  const currentFloorplan = useMemo(() => {
    if (!rootSpaceId) {
      return undefined;
    }
    return floorplans.find(
      (fp) =>
        fp.space.id === rootSpaceId ||
        fp.space.id === sceneAncestors?.scene?.space.id ||
        fp.space.descendantSpaces.includes(sceneAncestors?.scene?.space.id),
    );
  }, [rootSpaceId, floorplans, sceneAncestors]);

  // Get floorplan and scenes data for the DrawingToolContainer
  const { drawing, scenes } = useFloorplanData(
    currentFloorplan?.id,
    currentFloorplan?.space.id,
  );

  const [shotByIds, setShotByIds] = useState<Record<string, Shot>>({});
  const [version, setVersion] = useState<number>(1);
  useEffect(() => {
    if (rootSpaceId != null && referenceBatch != null) {
      loadDefaultScene(rootSpaceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadDefaultScene, rootSpaceId]);

  const arrowSceneNavigation = React.useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") {
        setNextScene();
      } else if (event.key === "ArrowLeft") {
        setPreviousScene();
      } else if (event.key === "ArrowUp") {
        moveSceneUp();
      } else if (event.key === "ArrowDown") {
        moveSceneDown();
      }
    },
    [moveSceneDown, moveSceneUp, setNextScene, setPreviousScene],
  );
  useEffect(() => {
    if (currentSceneId != null) {
      addEventListener("keydown", arrowSceneNavigation);
      return () => {
        removeEventListener("keydown", arrowSceneNavigation);
      };
    }
  }, [currentSceneId, arrowSceneNavigation]);

  const handleOnSelect = useCallback(
    (node: DisplayTreeNode) => {
      setVersion((prev) => prev + 1);

      setCurrentScene(node.parentKey);
      const batch = shotByIds[node.key]?.batch;
      if (batch === undefined) {
        return;
      }
      setReferenceBatch(batch);
      // // hack to set it to something rather than nothing
      setComparisonBatch(batch);
    },
    [
      setComparisonBatch,
      setCurrentScene,
      setReferenceBatch,
      shotByIds,
      setVersion,
    ],
  );

  const handleOnLoadShots = useCallback((shots: Shot[]) => {
    setShotByIds((s) => ({ ...s, ..._.keyBy(shots, "id") }));
  }, []);

  if (rootSpaceId === undefined) {
    return (
      <Empty>
        <SpaceSelectionModal
          buttonContent="Select A root space"
          onSelect={setRootSpace}
        />
      </Empty>
    );
  }

  return (
    <LayoutWithSideDrawer
      spotlightQAContent={
        referenceBatch != null && (
          <SpotlightQA
            batchId={referenceBatch.id}
            rootSpaceId={rootSpaceId}
            project={project}
            onSpotlightSelect={focusOnSpotlight}
          />
        )
      }
      arrowsContent={
        <SpacesTree
          showScenes
          showShots
          draggableScenes
          onSelect={handleOnSelect}
          onLoadShots={handleOnLoadShots}
          version={version}
          setVersion={setVersion}
        />
      }
      floorplansContent={
        drawing && scenes ? (
          <FloorplanViewer
            scenes={scenes}
            selectedSceneIds={
              currentSceneId?.value ? [currentSceneId.value] : []
            }
            drawing={drawing}
            onSelectScene={setCurrentScene}
            floorplanId={currentFloorplan?.id}
          />
        ) : (
          <Empty description="No floorplan data available"></Empty>
        )
      }
      onCollapse={(isCollapsed) => {
        if (isCollapsed) {
          focusOnSpotlight(undefined);
        }
      }}
    >
      <div className={styles.content}>
        <SpaceSelector
          project={project}
          rootSpaceId={rootSpaceId}
          onSelect={setRootSpace}
        />
        <div className={styles["viewer-pane"]}>
          <div className={styles.viewer}>
            {referenceBatch != null &&
              (currentSceneId?.loading ? (
                <Spin />
              ) : currentSceneId?.value != null ? (
                <DualViewer
                  sceneId={currentSceneId.value}
                  referenceBatch={referenceBatch}
                  comparisonBatch={comparisonBatch}
                  view={view}
                  onChangeView={setView}
                  focusedSpotlightId={focusedSpotlightId}
                  arrowSceneNavigation={arrowSceneNavigation}
                />
              ) : (
                <Empty description="No scenes available"></Empty>
              ))}
          </div>
          <BatchSelector
            referenceBatch={referenceBatch}
            comparisonBatch={comparisonBatch}
            onChangeReferenceBatch={(refId) => {
              const oldReferenceBatch = referenceBatch;
              setReferenceBatch(refId);
              // if the referenceBatch changes from null to a non-null
              // value then this might change the default scene, so load it again
              // But we don't want to do this on every referenceBatch change,
              // because then it would always jump back to the default scene
              if (!oldReferenceBatch && rootSpaceId) {
                loadDefaultScene(rootSpaceId);
              }
            }}
            onChangeComparisonBatch={setComparisonBatch}
          />
        </div>
        {/* need to pass scene's ancestor data here */}
        {currentSceneId?.value != null && (
          <SceneSelectionDropdowns
            rootSpaceId={rootSpaceId}
            sceneId={currentSceneId.value}
            onSelect={setCurrentScene}
          />
        )}
      </div>
    </LayoutWithSideDrawer>
  );
};

export const DualModePage = () => (
  <Provider>
    <DualModeContent />
  </Provider>
);
